import {
  API_ACCEPT,
  AuthApiPath,
  CONTENT_TYPE_FORM,
} from "@/lib/auth/constants";
import newrelic from "@/lib/monitoring/newrelic";
import { getAuthQueryParams } from "@/lib/auth/getAuthQueryParams";
import { buildApiEndpoint } from "@/lib/auth/buildApiEndpoint";
import { QueryParams } from "@/utils/functions/buildSafeUrl";
import { evaluateFeatureFlag } from "@/lib/feature-flags/evaluateFeatureFlag";

export enum TPALogistrationPage {
  //   is_register_page (bool): Determine the call is from register or login page
  RegisterOrLogin = "is_register_page",
  //   is_welcome_page (bool): Checks if the call is from the Welcome Page
  Welcome = "is_welcome_page",
}

export type Provider = {
  id: string | null;
  name: string | null;
  iconClass: string | null;
  iconImage: string | null;
  skipHintedLogin: boolean;
  skipRegistrationForm: boolean;
  loginUrl: string | null;
  registerUrl: string | null;
};

export type PipelineUserDetails = {
  username: string | null;
  email: string | null;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
};

type ContextData = {
  currentProvider: string | null;
  platformName: string | null;
  providers: Provider[];
  secondaryProviders: Provider[];
  finishAuthUrl: string | null;
  errorMessage: string | null;
  registerFormSubmitButtonText: string | null;
  autoSubmitRegForm: boolean;
  syncLearnerProfileData: boolean;
  countryCode: string | null;
  welcomePageRedirectUrl: string | null;
  pipelineUserDetails: PipelineUserDetails;
};

export type MFEContextResponse = {
  contextData: ContextData;
  registrationFields: Record<string, any>;
  optionalFields: {
    fields?: Record<string, any>;
    extended_profile: string[];
  };
};

/**
 * Returns a list of third party auth providers.
 *
 * If the user is already logged in via one, it will also be returned in this call.
 */
export default async function getThirdPartyAuthProviders(
  page: TPALogistrationPage = TPALogistrationPage.RegisterOrLogin,
  isModalContext: boolean = false,
) {
  const startTime = Date.now();
  const urlParameters = getAuthQueryParams();

  const queryParams: QueryParams = {
    ...urlParameters,
    [page]: "true",
  };

  if (typeof window !== "undefined") {
    if (isModalContext) {
      queryParams.next = window.location.href;
    } else {
      const isOnRegisterPage =
        window.location.pathname.includes("/auth/register");
      if (isOnRegisterPage) {
        try {
          const featureFlagValue = await evaluateFeatureFlag(
            "NEXT_PUBLIC_FEATURE_REGISTER_NEXT_URL_ENABLED",
            "false",
          );
          if (featureFlagValue === "true") {
            queryParams.next = window.location.href;
          }
        } catch (error) {
          newrelic.noticeError(error as Error, {
            submodule: "auth",
            context: "registerNextUrlFeatureFlag",
            timestamp: new Date().toISOString(),
          });
        }
      }
    }
  }

  try {
    const headers = {
      accept: API_ACCEPT,
      "Content-Type": CONTENT_TYPE_FORM,
    } as Record<string, string>;

    const url = buildApiEndpoint(AuthApiPath.Mfe3rdPartyContext, {
      qps: queryParams,
    });

    const response = await fetch(url, {
      headers: {
        ...headers,
      },
      method: "GET",
      credentials: "include",
    });

    const responseTime = Date.now() - startTime;

    if (!response.ok) {
      const error = new Error(
        `Third party auth providers API failed: ${response.status}`,
      );

      newrelic.noticeError(error, {
        submodule: "auth",
        endpoint: "ThirdPartyAuth",
        status: response.status,
        responseTime: responseTime,
        timestamp: new Date().toISOString(),
      });

      // Throw error to be handled by catch block with fallback response
      throw error;
    }

    const json = await response.json();
    return json as MFEContextResponse;
  } catch (e) {
    const responseTime = Date.now() - startTime;

    // Network error logging
    newrelic.noticeError(e as Error, {
      submodule: "auth",
      endpoint: "ThirdPartyAuth",
      context: "networkError",
      responseTime: responseTime,
      timestamp: new Date().toISOString(),
    });

    // Allows auth flow to continue without TPA options even on network errors
    return {
      contextData: {
        currentProvider: null,
        platformName: null,
        providers: [], // Empty array - no TPA options will show
        secondaryProviders: [],
        finishAuthUrl: null,
        errorMessage: null,
        registerFormSubmitButtonText: null,
        autoSubmitRegForm: false,
        syncLearnerProfileData: false,
        countryCode: null,
        welcomePageRedirectUrl: null,
        pipelineUserDetails: {
          username: null,
          email: null,
          name: null,
          firstName: null,
          lastName: null,
        },
      },
      registrationFields: {},
      optionalFields: {
        fields: {},
        extended_profile: [],
      },
    } as MFEContextResponse;
  }
}
